<template>
    <div>
      <Header />
  
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <button type="button" @click="$router.go(-1)" class="btn-back">
            <font-awesome-icon icon="arrow-left" /> Back
          </button>
          <h5 class="page-title mt-2">New Journal Posting</h5>
          <p class="page-sub">Create a new debit/credit journal entry</p>
        </div>
      </div>
  
      <!-- Validation Errors -->
      <div v-if="validationErrors?.length" class="error-banner">
        <font-awesome-icon icon="exclamation-circle" class="error-icon" />
        <ul class="mb-0 ps-1">
          <li v-for="(err, i) in validationErrors" :key="i">{{ err }}</li>
        </ul>
      </div>
  
      <form @submit.prevent="createData">
  
        <!-- Posting Details -->
        <div class="form-card mb-4">
          <div class="section-header">
            <div class="section-icon section-icon--blue">
              <font-awesome-icon icon="file-alt" />
            </div>
            <div>
              <p class="section-title">Posting Details</p>
              <p class="section-sub">Fill in the transaction information below</p>
            </div>
          </div>
  
          <div class="fields-grid">
  
            <div class="field-group">
              <label class="field-label">Transaction Date <span class="req">*</span></label>
              <div class="datepicker-wrap">
                <font-awesome-icon icon="calendar" class="datepicker-icon" />
                <Datepicker v-model="journal.transaction_date" :enableTimePicker="true" class="clean-datepicker" />
              </div>
            </div>
  
            <div class="field-group">
              <label class="field-label">Transaction Type <span class="req">*</span></label>
              <select class="field-input field-select" v-model="journal.transaction_type">
                <option value="" disabled>Choose type</option>
                <option value="debit">Debit</option>
                <option value="credit">Credit</option>
              </select>
            </div>
  
            <div class="field-group">
              <label class="field-label">Debit Account <span class="req">*</span></label>
              <select v-model="journal.debit_account" class="field-input field-select">
                <option disabled value="">Select account</option>
                <optgroup v-for="(accs, type) in accounts" :key="type" :label="capitalize(type)">
                  <option v-for="acc in accs" :key="acc.id" :value="acc.id">{{ acc.name }}</option>
                </optgroup>
              </select>
            </div>
  
            <div class="field-group">
              <label class="field-label">Credit Account <span class="req">*</span></label>
              <select v-model="journal.credit_account" class="field-input field-select">
                <option disabled value="">Select account</option>
                <optgroup v-for="(accs, type) in accounts" :key="type" :label="capitalize(type)">
                  <option v-for="acc in accs" :key="acc.id" :value="acc.id">{{ acc.name }}</option>
                </optgroup>
              </select>
            </div>
  
            <div class="field-group">
              <label class="field-label">Debit Amount <span class="req">*</span></label>
              <input type="text" class="field-input" v-model="journal.debit_amount"
                onkeypress="if(isNaN(this.value+String.fromCharCode(event.keyCode)))return false;"
                placeholder="0.00" />
            </div>
  
            <div class="field-group">
              <label class="field-label">Credit Amount <span class="req">*</span></label>
              <input type="text" class="field-input" v-model="journal.credit_amount"
                onkeypress="if(isNaN(this.value+String.fromCharCode(event.keyCode)))return false;"
                placeholder="0.00" />
              <span class="field-error" v-if="journal.debit_amount && journal.credit_amount && journal.debit_amount != journal.credit_amount">
                Debit and credit amounts must match
              </span>
            </div>
  
            <div class="field-group field-group--full">
              <label class="field-label">Description</label>
              <textarea class="field-input" v-model="journal.description" rows="3" placeholder="Optional description for this posting"></textarea>
            </div>
  
          </div>
        </div>
  
        <!-- Amount Match Indicator -->
        <div class="balance-indicator" v-if="journal.debit_amount && journal.credit_amount">
          <div class="balance-side">
            <span class="balance-label">Debit</span>
            <span class="balance-value">{{ journal.debit_amount }}</span>
          </div>
          <div class="balance-eq" :class="journal.debit_amount == journal.credit_amount ? 'balance-eq--ok' : 'balance-eq--err'">
            <font-awesome-icon :icon="journal.debit_amount == journal.credit_amount ? 'check-circle' : 'times-circle'" />
            {{ journal.debit_amount == journal.credit_amount ? 'Balanced' : 'Unbalanced' }}
          </div>
          <div class="balance-side balance-side--right">
            <span class="balance-label">Credit</span>
            <span class="balance-value">{{ journal.credit_amount }}</span>
          </div>
        </div>
  
        <!-- Footer -->
        <div class="form-footer">
          <button type="button" @click="$router.go(-1)" class="btn-outline">Cancel</button>
          <button type="submit" class="btn-primary-action"
            :disabled="crudLoading || journal.debit_amount != journal.credit_amount">
            <span v-if="crudLoading" class="spinner-border spinner-border-sm me-2" role="status"></span>
            <font-awesome-icon v-else icon="check" class="me-2" />
            {{ crudLoading ? 'Processing...' : 'Submit Posting' }}
          </button>
        </div>
  
      </form>
    </div>
  </template>
  
  <script>
  import axios from 'axios';
  import { useToast } from 'vue-toastification';
  import { useRouter } from 'vue-router';
  import { useStore } from 'vuex';
  import Header from './component/header.vue';
  
  export default {
    components: { Header },
  
    data() {
      return {
        toast:            useToast(),
        router:           useRouter(),
        store:            useStore(),
        validationErrors: [],
        accounts:         {},
        crudLoading:      false,
        loading:          false,
  
        journal: {
          transaction_date:  new Date(),
          debit_account:     '',
          credit_account:    '',
          debit_amount:      0.00,
          credit_amount:     0.00,
          description:       '',
          transaction_type:  '',
        },
      };
    },
  
    computed: {
      permission() { return this.store.state.getCurrentUserPermission; },
    },
  
    created() { this.fetchCategory(); },
  
    methods: {
      async fetchCategory() {
        axios.get('/accounting/active-ledgers')
          .then(res => { this.accounts = res?.data?.data; })
          .catch(() => {});
      },
  
      async createData() {
        if (this.journal.debit_amount != this.journal.credit_amount) {
          this.toast.error('Debit and credit amounts must match'); return;
        }
        this.crudLoading = true;
        axios.post('accounting/journal', this.journal)
          .then(response => {
            this.crudLoading = false;
            if (response.data.status === 200) {
              this.toast.success(response.data.message);
              this.$router.push({ path: '/manage-journals' });
            } else {
              this.toast.error(response.data.message);
            }
          })
          .catch(error => {
            this.crudLoading = false;
            const msg = error.response?.data?.message;
            if (Array.isArray(msg)) { this.validationErrors = msg; }
            else this.toast.error(msg ?? 'Something went wrong');
          });
      },
  
      capitalize(text) { return text.charAt(0).toUpperCase() + text.slice(1); },
    },
  };
  </script>
  
  <style scoped>
  /* ── Page Header ── */
  .page-header { margin-bottom:20px; }
  .page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
  .page-sub    { font-size:13px; color:#7a90b0; margin:0; }
  .btn-back    { display:inline-flex; align-items:center; gap:7px; background:transparent; border:none; color:#7a90b0; font-size:13px; cursor:pointer; padding:0; margin-bottom:4px; transition:color .15s; }
  .btn-back:hover { color:#1a2b45; }
  
  /* ── Error Banner ── */
  .error-banner { display:flex; align-items:flex-start; gap:10px; background:#fff5f5; border:1px solid #fac5c5; border-radius:10px; padding:12px 16px; margin-bottom:20px; color:#e03535; font-size:13px; }
  .error-icon   { font-size:16px; margin-top:2px; flex-shrink:0; }
  
  /* ── Form Card ── */
  .form-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:24px; }
  .mb-4      { margin-bottom:16px; }
  
  /* ── Section Header ── */
  .section-header { display:flex; align-items:center; gap:14px; margin-bottom:20px; }
  .section-icon   { width:42px; height:42px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:17px; flex-shrink:0; }
  .section-icon--blue { background:rgba(0,74,173,.1); color:#004AAD; }
  .section-title  { font-size:14px; font-weight:700; color:#1a2b45; margin:0 0 2px; }
  .section-sub    { font-size:12px; color:#7a90b0; margin:0; }
  
  /* ── Fields ── */
  .fields-grid   { display:grid; grid-template-columns:repeat(3,1fr); gap:16px; }
  .field-group   { display:flex; flex-direction:column; gap:6px; }
  .field-group--full { grid-column:1/-1; }
  .field-label   { font-size:13px; font-weight:600; color:#1a2b45; }
  .req           { color:#e03535; }
  .field-error   { font-size:11.5px; color:#e03535; }
  .field-input   { padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13.5px; color:#1a2b45; background:#fff; outline:none; width:100%; transition:border-color .15s; font-family:inherit; resize:vertical; }
  .field-input:focus  { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
  .field-select { appearance:auto; cursor:pointer; }
  
  /* ── Datepicker ── */
  .datepicker-wrap { position:relative; }
  .datepicker-icon { position:absolute; left:11px; top:50%; transform:translateY(-50%); color:#7a90b0; font-size:13px; z-index:2; pointer-events:none; }
  :deep(.clean-datepicker) { border:1px solid #dde5f0 !important; border-radius:8px !important; background:#fff !important; padding-left:32px !important; }
  :deep(.clean-datepicker input) { font-size:13.5px; color:#1a2b45; }
  :deep(.clean-datepicker.is-active) { border-color:#004AAD !important; box-shadow:0 0 0 3px rgba(0,74,173,.08) !important; }
  
  /* ── Balance Indicator ── */
  .balance-indicator {
    display:flex; align-items:center; justify-content:center; gap:24px;
    background:#fff; border:1px solid #e4eaf2; border-radius:12px;
    padding:16px 24px; margin-bottom:16px;
  }
  .balance-side       { display:flex; flex-direction:column; align-items:center; gap:4px; }
  .balance-side--right { align-items:center; }
  .balance-label      { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; }
  .balance-value      { font-size:20px; font-weight:800; color:#1a2b45; }
  .balance-eq         { display:flex; align-items:center; gap:8px; font-size:13.5px; font-weight:700; padding:8px 20px; border-radius:8px; flex-direction:column; }
  .balance-eq--ok     { color:#16a34a; background:rgba(22,163,74,.08); }
  .balance-eq--err    { color:#e03535; background:rgba(224,53,53,.08); }
  .balance-eq svg     { font-size:22px; }
  
  /* ── Footer ── */
  .form-footer { display:flex; align-items:center; justify-content:flex-end; gap:10px; margin-top:4px; }
  .btn-primary-action { display:inline-flex; align-items:center; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:10px 28px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; }
  .btn-primary-action:hover:not(:disabled) { background:#003b8e; }
  .btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }
  .btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:9px 20px; border-radius:8px; cursor:pointer; transition:background .15s; }
  .btn-outline:hover { background:#f4f7fb; }
  
  /* ── Responsive ── */
  @media(max-width:991px) { .fields-grid { grid-template-columns:repeat(2,1fr); } }
  @media(max-width:576px) {
    .fields-grid { grid-template-columns:1fr; }
    .form-card   { padding:16px; }
    .form-footer { flex-direction:column; }
    .btn-primary-action,.btn-outline { width:100%; justify-content:center; }
    .balance-indicator { flex-direction:column; gap:12px; }
  }
  </style>